import { SyntheticEvent, useCallback, useMemo, useRef } from 'react';

import useClickOutside from 'hooks/useClickOutside';
import useModal from 'hooks/useModal';

import {
  HandleOpenMenu,
  MenuItemClickHandler,
  MenuRef,
  MenuWithVisibleItems,
  Props,
} from './types';

type ReusedProps = 'menuConfig' | 'data' | 'onClick' | 'renderAsTooltip';

const useTableMenu = <T>({
  menuConfig,
  data,
  onClick,
  renderAsTooltip,
}: Pick<Props<T>, ReusedProps>) => {
  // region *******************************DATA*********************************
  const menuRef: MenuRef = useRef<Nullable<HTMLDivElement>>(null);

  const [isMenuOpened, handleToggleMenu] = useModal();

  const menuWithVisibleItems: MenuWithVisibleItems<T> = useMemo(
    () => menuConfig?.filter((e) => (e?.isHidden ? !e.isHidden(data) : true)),
    [data, menuConfig]
  );
  // endregion

  // region *****************************CALLBACKS******************************
  const handleOpenMenu: HandleOpenMenu = useCallback(
    (ev) => {
      if (renderAsTooltip) {
        ev.stopPropagation();
      }
      ev.preventDefault();
      handleToggleMenu();
    },
    [handleToggleMenu, renderAsTooltip]
  );

  const menuItemClickHandler: MenuItemClickHandler = useCallback(
    (accessor: string, isDisabled: boolean) => (e: SyntheticEvent) => {
      e.stopPropagation();
      if (isDisabled) return;
      onClick(accessor, data, e);
      handleToggleMenu();
    },
    [data, handleToggleMenu, onClick]
  );
  // endregion

  // region ******************************EFFECTS*******************************
  useClickOutside(menuRef, isMenuOpened ? handleToggleMenu : undefined);
  // endregion

  return useMemo(
    () => ({
      menuWithVisibleItems,
      handleOpenMenu,
      menuItemClickHandler,
      menuRef,
      isMenuOpened,
    }),
    [handleOpenMenu, isMenuOpened, menuItemClickHandler, menuWithVisibleItems]
  );
};

export default useTableMenu;
